import { isArray, mergeWith } from 'lodash-es';

import { CelumProperties } from './celum-properties.model';

/** Expected global object */
declare let Celum: { properties: CelumProperties };

const helpCenterDefaultUrl = 'https://help-dev.celum.net/user/?l=en';
const privacyPolicyDefaultUrl = 'https://www.celum.com/en/privacy-policy/';
const termsConditionsDefaultUrl = 'https://help-dev.celum.net/legal/celum-terms-of-service/?l=en';

const defaultCelumProperties: CelumProperties = {
  defaultLanguage: 'en',
  availableLanguages: ['en', 'de', 'fr', 'ja'],
  translationPath: '__pathToBuild__',
  ng2ProdMode: false,
  buildTimestamp: '',
  httpBaseAddress: 'http://localhost:8091/frontend',
  sharingHttpBaseAddress: 'http://localhost:8090',
  librariesHttpBaseAddress: 'http://localhost:8090',
  librariesUrl: 'http://localhost:8080',
  experienceHttpBaseAddress: 'http://localhost:8090',
  experienceUrl: 'http://localhost:8080',
  supportPortalUrl: 'https://service.celum.com/plugins/servlet/desk',
  logCommunication: false,
  logSendMessages: true,
  logPerformanceMeasurements: true,
  liveUpdateInterval: 60000,
  cloudOrganizationId: '',

  tutorialVideoLink: 'http://www.123contactform.com',
  helpLinks: {
    en: 'http://www.123contactform.com/index.html',
    de: 'http://www.123contactform.com/index.html',
    fr: 'http://www.123contactform.com/index.html'
  },
  privacyPolicyLink: 'http://www.123contactform.com',
  termsConditionsLink: 'http://www.123contactform.com',

  autoUpdateQueries: true,

  logLevel: 4,
  consoleLogLevel: 3,
  stackTraceLines: 40, // make sure not to miss the actual source of the issue

  queryRetryDelay: 5000,
  queryRetries: 3,
  queryRetryForConnectionLossOnly: false,
  pingInterval: 3500,
  parentPathTimeout: 1000 * 60 * 5, // 5 minutes
  pingAddress: '',
  uiLogsReportInterval: 10000,
  redirectToUrlOn401: '',
  redirectToSelfOn403: false,
  maxConcurrentUploads: 5,
  connectionCheck: true,
  disableSpaceAwareTooltipOnSafari: true,
  appProperties: {},
  authentication: {
    clientId: '',
    b2cDomain: 'login-dev.celum.net',
    b2cTenantId: '',
    aDDomain: 'celumdev',
    redirectUri: 'http://localhost:4200',
    redirectTokenUri: 'http://localhost:4200',
    logoutRedirectUri: 'http://localhost:4200',
    defaultSigninPolicy: 'B2C_1A_signup_signin',
    saccUrl: 'https://api-dev.celum.net/internal/sacc/latest',
    saccAccountMemberUrl: 'https://sacc-dev.celum.net/account-membership/$accountId$/members',
    applicationLicenseType: '',
    applicationFallbackPagesPerLanguage: {},
    useCookieBasedResourceAuthentication: false
  },
  applicationName: '',
  systemBar: {
    contentHubFallbackUrlPerLanguage: {
      en: 'https://www.celum.com/${language}/digital-asset-management-software/',
      de: 'https://www.celum.com/${language}/digital-asset-management-software/',
      fr: 'https://www.celum.com/${language}/digital-asset-management-software/',
      ja: 'https://www.celum.com/${language}/digital-asset-management-software/'
    },
    librariesFallbackUrlPerLanguage: {
      en: 'https://www.celum.com/en/media-portals-libraries/',
      de: 'https://www.celum.com/de/media-portale-libraries/',
      fr: 'https://www.celum.com/en/media-portals-libraries/',
      ja: 'https://www.celum.com/en/media-portals-libraries/'
    },
    experienceFallbackUrlPerLanguage: {
      en: 'https://www.celum.com/en/media-portals-libraries/',
      de: 'https://www.celum.com/de/media-portale-libraries/',
      fr: 'https://www.celum.com/en/media-portals-libraries/',
      ja: 'https://www.celum.com/en/media-portals-libraries/'
    },
    workAppUrl: 'https://workrooms-dev.celum.net',
    librariesAppUrl: 'https://libraries-dev.celum.net',
    experienceAppUrl: 'https://experience-dev.celum.net',
    helpUrls: {
      helpCenter: {
        en: helpCenterDefaultUrl,
        de: 'https://help-dev.celum.net/user/?l=de',
        fr: helpCenterDefaultUrl,
        ja: helpCenterDefaultUrl
      },
      privacyPolicy: {
        en: privacyPolicyDefaultUrl,
        de: 'https://www.celum.com/de/datenschutzerklaerung/',
        fr: privacyPolicyDefaultUrl,
        ja: privacyPolicyDefaultUrl
      },
      termsConditions: {
        en: termsConditionsDefaultUrl,
        de: 'https://help-dev.celum.net/legal/celum-terms-of-service/?l=de',
        fr: termsConditionsDefaultUrl,
        ja: termsConditionsDefaultUrl
      }
    },
    showLanguageSelectorInAvatarMenu: false,
    // remove after release of 27.3
    share: {
      featureEnabled: true,
      httpBaseAddress: 'https://share-dev.celum.net'
    }
  },
  applicationInsights: {
    instrumentationKey: '',
    enableAutoRouteTracking: false,
    endpointUrl: 'https://analytics.celum.net',
    samplingPercentage: null
  }
};

/** Used as temporary var to merge Global and Default properties */
let tempCelumProperties: any;
let globalCelumObjectExists = true;

try {
  // eslint-disable-next-line no-unused-expressions
  Celum;
} catch (err) {
  globalCelumObjectExists = false;
}

if (!globalCelumObjectExists) {
  console.debug('CelumProperties: using default values!');
  // provide default values...
  tempCelumProperties = defaultCelumProperties;
} else {
  if (Celum.properties.consoleLogLevel <= 1) {
    console.debug('CelumProperties: merging configured properties with defaults...');
  }

  // Use the default properties as base and deep-merge in all properties.js values
  // To get type-support when adding new properties, also add those in the CelumProperties interface
  tempCelumProperties = mergeWith({}, defaultCelumProperties, Celum.properties, (objectValue, srcValue) => {
    if (isArray(objectValue)) {
      return srcValue; // overwrite arrays instead of merging
    }
  });

  Celum.properties = tempCelumProperties;
}

/**
 * Runtime properties for celum application
 */
export class CelumPropertiesProvider {
  static get properties(): CelumProperties {
    return (globalThis as any).Celum?.properties ?? defaultCelumProperties;
  }
}
